import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { usePricing } from '../contexts/PricingContext';

// PlanCard Component
const PlanCard = ({ plan, isBestValue }) => {
  return (
    <div className={`relative bg-white rounded-2xl shadow-md p-8 transition-all duration-300 hover:shadow-lg ${
      isBestValue ? 'border-2 border-blue-500 scale-105' : 'border border-gray-200'
    }`}>
      {/* Best Value Badge */}
      {isBestValue && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
            Best Value
          </div>
        </div>
      )}

      {/* Card Header */}
      <div className="flex justify-between items-center mb-6">
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          plan.name === 'BASIC' ? 'bg-green-100 text-green-800' :
          plan.name === 'VECTOR PRO' ? 'bg-blue-100 text-blue-800' :
          'bg-orange-100 text-orange-800'
        }`}>
                      {plan.name === 'BASIC' ? 'Basic' :
            plan.name === 'VECTOR PRO' ? 'Professional' : 'Expert'}
        </div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          plan.name === 'BASIC' ? 'bg-green-500' :
                      plan.name === 'VECTOR PRO' ? 'bg-blue-500' :
          'bg-orange-500'
        }`}>
          <div className="w-4 h-4 bg-white rounded-full"></div>
        </div>
      </div>

      {/* Price Display */}
      <div className="mb-6">
        {plan.promoPrice !== null && plan.promoPrice !== undefined && parseFloat(plan.promoPrice) !== parseFloat(plan.price) ? (
          <>
            {/* With Discount */}
            <div className="flex items-baseline mb-2">
              <span className="text-gray-600 text-lg">₹</span>
              <span className="text-5xl font-bold text-gray-900 mx-1">{parseFloat(plan.promoPrice).toFixed(0)}</span>
              <span className="text-gray-600 text-lg">.{(parseFloat(plan.promoPrice) % 1).toFixed(2).split('.')[1]}</span>
            </div>
            <div className="flex items-baseline mb-1">
              <span className="text-gray-500 text-lg">₹</span>
              <span className="text-xl font-semibold text-gray-500 line-through mx-1">{parseFloat(plan.price).toFixed(0)}</span>
              <span className="text-gray-500 text-lg">.00</span>
            </div>
          </>
        ) : (
          <>
            {/* No Discount */}
            <div className="flex items-baseline mb-2">
              <span className="text-gray-600 text-lg">₹</span>
              <span className="text-5xl font-bold text-gray-900 mx-1">{parseFloat(plan.price).toFixed(0)}</span>
              <span className="text-gray-600 text-lg">.00</span>
            </div>
          </>
        )}
        <p className="text-gray-600 text-sm">{plan.subtitle}</p>
      </div>

      {/* Buy Now Button */}
      <Link 
        to="/pricing"
        className={`block w-full py-3 px-6 rounded-full text-white font-semibold text-center transition-all duration-300 hover:shadow-lg ${
          plan.name === 'BASIC' ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' :
                      plan.name === 'VECTOR PRO' ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700' :
          'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
        }`}
      >
        Start 7-Day Free Trial
      </Link>

      {/* Features List */}
      <div className="mt-6">
        <ul className="space-y-3">
          {plan.features.slice(0, 4).map((feature, index) => (
            <li key={index} className="flex items-center">
              <FaCheckCircle className={`mr-3 flex-shrink-0 ${
                plan.name === 'BASIC' ? 'text-green-500' :
                plan.name === 'VECTOR PRO' ? 'text-blue-500' :
                'text-orange-500'
              }`} />
              <span className="text-gray-700 text-sm">{feature.text}</span>
            </li>
          ))}
          {plan.features.length > 4 && (
            <li className="text-gray-500 text-sm text-center pt-2">
              +{plan.features.length - 4} more features
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

// Main PricingSection Component
const PricingSection = () => {
  const { activeOffer, plans: dynamicPlans, loading, refreshPricing } = usePricing();

  // Refresh pricing data when page becomes visible (user switches back to tab)
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && refreshPricing) {
        console.log('📊 Landing page became visible, refreshing pricing data...');
        refreshPricing();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshPricing]);

  // Show loading state
  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </section>
    );
  }

  // Transform dynamic plans to match component's expected structure
  const plans = dynamicPlans.map((plan) => {
    // Transform features array to include 'included' property
    const features = plan.features.map(feature => ({
      text: feature,
      included: true
    }));

    return {
      name: plan.name,
      subtitle: plan.display_name,
      price: plan.original_price.toString(),
      promoPrice: plan.promo_price !== null && plan.promo_price !== undefined ? plan.promo_price.toString() : null,
      planType: 'MONTHLY PLAN',
      features,
      colors: {
        headerBg: plan.name === 'BASIC' ? 'bg-[#04A7A7]' : plan.name === 'VECTOR PRO' ? 'bg-[#1D3557]' : 'bg-[#F46036]',
        buttonBg: plan.name === 'BASIC' ? 'bg-[#04A7A7]' : plan.name === 'VECTOR PRO' ? 'bg-[#1D3557]' : 'bg-[#F46036]',
        check: plan.name === 'BASIC' ? 'text-[#04A7A7]' : plan.name === 'VECTOR PRO' ? 'text-[#1D3557]' : 'text-[#F46036]'
      }
    };
  });

  // Fallback to empty array if no plans
  if (plans.length === 0) {
    return null;
  }


  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          {/* 7-Day Free Trial Banner */}
          <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-lg mb-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-center space-x-3">
              <span className="text-2xl">🎉</span>
              <div>
                <h2 className="text-xl font-bold">New User Offer!</h2>
                <p className="text-lg">Select any plan and get your extra first 7 days completely free with full access to all EXPERT features.</p>
              </div>
              <span className="text-2xl">🎉</span>
            </div>
          </div>
          
          {/* Dynamic Promotional Banner - Only show if active offer exists */}
          {activeOffer && (
            <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-full inline-block mb-6">
              <span className="font-bold text-lg">🔥 {activeOffer.name} 🔥</span>
            </div>
          )}
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Choose the Plan That's Right for You
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start with our powerful algorithmic analysis and scale up as your trading needs grow. 
            
            <strong className="text-green-600"> New users get extra 7 days free trial with full EXPERT features!</strong>
            {activeOffer && activeOffer.discount_percent && (
              <span className="block mt-2 text-red-600 font-bold">
                Limited time: {activeOffer.discount_percent}% OFF all plans!
              </span>
            )}
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <PlanCard 
              key={plan.name}
              plan={plan}
              isBestValue={plan.name === 'VECTOR PRO'} // Highlight VECTOR PRO as best value
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-6">
            Experience the full power of TrendVector with our{' '}
            <span className="text-green-600 font-semibold">extra 7-day free trial</span>
            {' '}and get access to all EXPERT features including AI Stock Analyst, Swing Trading, and unlimited searches.
          </p>
          
          <Link 
            to="/pricing"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-full hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Start Your Free Trial Now
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
